import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-lg shadow-orange-500/30">
              <HyreDocsIcon className="w-6 h-6" />
            </div>
            <span className="font-heading font-bold text-2xl">HyreDocs</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-8" data-testid="forgot-password-form">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold">Check your email</h2>
              <p className="text-muted-foreground text-sm">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. 
                Check your inbox and spam folder.
              </p>
              <div className="pt-4">
                <Link to="/login" className="text-primary font-medium hover:underline text-sm">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <Mail className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <h2 className="font-heading text-xl font-semibold">Forgot your password?</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>

              <div className="text-center mt-6">
                <Link to="/login" className="text-primary font-medium hover:underline text-sm">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
